import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { endOfDay, startOfDay } from 'date-fns';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Messages } from 'primereact/messages';
import { Panel } from 'primereact/panel';
import { Calendar } from 'primereact/calendar';
import { SelectButton } from 'primereact/selectbutton';
import { DataTable as PrimeDataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { AppCard, AppPage } from '../../components/general';
import { toLocalDateTime } from '../../utils/date-helper';
import {
  VoxTracingSearchMode,
  WaterfallExecutionSearchResponse,
  VoxOrderHistorySearchResponse,
} from '../../models/voxTracing';
import {
  useWaterfallExecutionSearch,
  useVoxOrderHistorySearch,
} from '../../hooks/useVoxTracing';
import {
  getWaterfallExecutionDetail,
  getVoxOrderHistoryDetail,
} from '../../services/voxTracingService';
import styles from './VoxTracingSearch.module.css';

const searchModeOptions = [
  { label: 'Waterfall Executions', value: 'waterfall' },
  { label: 'VoX Orders', value: 'voxOrder' },
];

const VoxTracingSearch = () => {
  const navigate = useNavigate();
  const messages = useRef<any>(null);

  const [searchMode, setSearchMode] = useState<VoxTracingSearchMode>('waterfall');

  // Direct ID search
  const [directId, setDirectId] = useState('');

  // Common fields
  const [loanNumber, setLoanNumber] = useState('');
  const [loanIdentifier, setLoanIdentifier] = useState('');

  // Waterfall-specific
  const now = new Date();
  const start = startOfDay(new Date(now.getFullYear(), now.getMonth(), 1));
  const end = endOfDay(new Date(now.getFullYear(), now.getMonth() + 1, 0));
  const [startDate, setStartDate] = useState<Date | null>(start);
  const [endDate, setEndDate] = useState<Date | null>(end);

  // VoX Order-specific
  const [voxOrderId, setVoxOrderId] = useState('');
  const [externalId, setExternalId] = useState('');

  // Advanced search expanded
  const [advancedSearchExpanded, setAdvancedSearchExpanded] = useState(false);

  // Search state
  const [waterfallSearchEnabled, setWaterfallSearchEnabled] = useState(false);
  const [waterfallSearchParams, setWaterfallSearchParams] = useState<any>({});
  const [voxSearchEnabled, setVoxSearchEnabled] = useState(false);
  const [voxSearchParams, setVoxSearchParams] = useState<any>({});

  const { searchQuery: waterfallQuery } = useWaterfallExecutionSearch(waterfallSearchParams, waterfallSearchEnabled);
  const { searchQuery: voxQuery } = useVoxOrderHistorySearch(voxSearchParams, voxSearchEnabled);

  const [directSearchLoading, setDirectSearchLoading] = useState(false);

  const handleDirectSearch = useCallback(async () => {
    if (!directId || directId.trim().length === 0) {
      messages.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: `Please enter a ${searchMode === 'waterfall' ? 'Waterfall Execution Record' : 'VoX Order History'} Id`,
        life: 3000,
      });
      return;
    }

    const id = directId.trim();
    setDirectSearchLoading(true);
    try {
      if (searchMode === 'waterfall') {
        await getWaterfallExecutionDetail(id);
        navigate(`/vox-tracing/waterfall/${id}`);
      } else {
        await getVoxOrderHistoryDetail(id);
        navigate(`/vox-tracing/order/${id}`);
      }
    } catch (error) {
      messages.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error instanceof Error ? error.message : 'Failed to load transaction',
        life: 5000,
      });
    } finally {
      setDirectSearchLoading(false);
    }
  }, [directId, searchMode, navigate]);

  const handleSearch = () => {
    if (searchMode === 'waterfall') {
      const params: any = {};
      if (loanNumber.trim()) params.loanNumber = loanNumber.trim();
      if (loanIdentifier.trim()) params.loanIdentifier = loanIdentifier.trim();
      if (startDate && endDate) {
        params.startDate = startOfDay(startDate).toISOString();
        params.endDate = endOfDay(endDate).toISOString();
      }

      if (!params.loanNumber && !params.loanIdentifier) {
        messages.current?.show({
          severity: 'warn',
          summary: 'Warning',
          detail: 'Please enter at least a loan number or loan identifier',
          life: 3000,
        });
        return;
      }

      setWaterfallSearchParams(params);
      setWaterfallSearchEnabled(true);
      setVoxSearchEnabled(false);
    } else {
      const params: any = {};
      if (loanNumber.trim()) params.loanNumber = loanNumber.trim();
      if (loanIdentifier.trim()) params.loanIdentifier = loanIdentifier.trim();
      if (voxOrderId.trim()) params.voxOrderId = voxOrderId.trim();
      if (externalId.trim()) params.externalId = externalId.trim();

      if (!params.loanNumber && !params.loanIdentifier && !params.voxOrderId && !params.externalId) {
        messages.current?.show({
          severity: 'warn',
          summary: 'Warning',
          detail: 'Please enter at least one search criteria',
          life: 3000,
        });
        return;
      }

      setVoxSearchParams(params);
      setVoxSearchEnabled(true);
      setWaterfallSearchEnabled(false);
    }
  };

  const handleModeChange = (value: VoxTracingSearchMode) => {
    if (value) {
      setSearchMode(value);
      setWaterfallSearchEnabled(false);
      setVoxSearchEnabled(false);
    }
  };

  // Waterfall result handlers
  const handleWaterfallSelect = (e: any) => {
    const selected = e.value as WaterfallExecutionSearchResponse;
    if (selected?.waterfallExecutionRecordGuid) {
      navigate(`/vox-tracing/waterfall/${selected.waterfallExecutionRecordGuid}`);
    }
  };

  const formatWaterfallDateTime = (rowData: WaterfallExecutionSearchResponse) => {
    return rowData.dateStarted ? toLocalDateTime(rowData.dateStarted) : '';
  };

  const waterfallStatusTemplate = (rowData: WaterfallExecutionSearchResponse) => {
    const severityMap: Record<string, 'success' | 'info' | 'warning' | 'danger'> = {
      Complete: 'success',
      Running: 'info',
      Error: 'danger',
      Abort: 'warning',
    };
    return <Tag value={rowData.status} severity={severityMap[rowData.status] || 'info'} />;
  };

  // VoX Order result handlers
  const handleVoxOrderSelect = (e: any) => {
    const selected = e.value as VoxOrderHistorySearchResponse;
    if (selected?.voxOrderHistoryGuid) {
      navigate(`/vox-tracing/order/${selected.voxOrderHistoryGuid}`);
    }
  };

  const formatVoxDateTime = (rowData: VoxOrderHistorySearchResponse) => {
    return rowData.createdDate ? toLocalDateTime(rowData.createdDate) : '';
  };

  const voxStatusTemplate = (rowData: VoxOrderHistorySearchResponse) => {
    const severityMap: Record<string, 'success' | 'info' | 'warning' | 'danger'> = {
      Success: 'success',
      Pending: 'info',
      AwaitingCallback: 'info',
      Failed: 'danger',
      Error: 'danger',
      Timeout: 'warning',
    };
    return <Tag value={rowData.status} severity={severityMap[rowData.status] || 'info'} />;
  };

  const activeQuery = searchMode === 'waterfall' ? waterfallQuery : voxQuery;
  const isSearchActive = searchMode === 'waterfall' ? waterfallSearchEnabled : voxSearchEnabled;

  return (
    <AppPage title="VoX Transaction Logs" breadcrumbs="Home - VoX Transaction Logs">
      <Messages ref={messages} />

      <AppCard className={styles.voxTracingSearch}>
        <div className="flex flex-column gap-4">
          <div className="flex flex-column gap-2">
            <label className="font-semibold">Search Type</label>
            <SelectButton
              value={searchMode}
              onChange={(e) => handleModeChange(e.value)}
              options={searchModeOptions}
            />
          </div>

          <div className="flex flex-column gap-2">
            <label htmlFor="directId" className="font-semibold">
              {searchMode === 'waterfall' ? 'Search by Waterfall Execution Record Id' : 'Search by VoX Order History Id'}
            </label>
            <div className="flex gap-2">
              <InputText
                id="directId"
                value={directId}
                onChange={(e) => setDirectId(e.target.value)}
                placeholder={searchMode === 'waterfall' ? 'Paste waterfall execution record GUID...' : 'Paste VoX order history GUID...'}
                className="flex-1"
                style={{ backgroundColor: '#f9fafb' }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleDirectSearch();
                  }
                }}
              />
              <Button
                label="Go"
                icon="pi pi-search"
                onClick={handleDirectSearch}
                disabled={!directId || directId.trim().length === 0}
                loading={directSearchLoading}
              />
            </div>
          </div>

          <Panel
            header="Advanced Search"
            toggleable
            collapsed={!advancedSearchExpanded}
            onToggle={(e) => setAdvancedSearchExpanded(!e.value)}
          >
          <div className="flex flex-column gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-column gap-2">
                <label htmlFor="loanNumber">Loan Number</label>
                <InputText
                  id="loanNumber"
                  value={loanNumber}
                  onChange={(e) => setLoanNumber(e.target.value)}
                  placeholder="Enter loan number"
                  style={{ backgroundColor: '#f9fafb' }}
                  onKeyPress={(e) => { if (e.key === 'Enter') handleSearch(); }}
                />
              </div>

              <div className="flex flex-column gap-2">
                <label htmlFor="loanIdentifier">Loan Identifier</label>
                <InputText
                  id="loanIdentifier"
                  value={loanIdentifier}
                  onChange={(e) => setLoanIdentifier(e.target.value)}
                  placeholder="Enter loan identifier"
                  style={{ backgroundColor: '#f9fafb' }}
                  onKeyPress={(e) => { if (e.key === 'Enter') handleSearch(); }}
                />
              </div>
            </div>

            {searchMode === 'waterfall' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-column gap-2">
                  <label htmlFor="startDate">Start Date</label>
                  <Calendar
                    id="startDate"
                    value={startDate}
                    onChange={(e) => setStartDate(e.value as Date)}
                    showIcon
                    dateFormat="mm/dd/yy"
                    placeholder="Select start date"
                    inputStyle={{ backgroundColor: '#f9fafb' }}
                    iconPos="right"
                  />
                </div>
                <div className="flex flex-column gap-2">
                  <label htmlFor="endDate">End Date</label>
                  <Calendar
                    id="endDate"
                    value={endDate}
                    onChange={(e) => setEndDate(e.value as Date)}
                    showIcon
                    dateFormat="mm/dd/yy"
                    placeholder="Select end date"
                    inputStyle={{ backgroundColor: '#f9fafb' }}
                    iconPos="right"
                  />
                </div>
              </div>
            )}

            {searchMode === 'voxOrder' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-column gap-2">
                  <label htmlFor="voxOrderId">VoX Order ID (GUID)</label>
                  <InputText
                    id="voxOrderId"
                    value={voxOrderId}
                    onChange={(e) => setVoxOrderId(e.target.value)}
                    placeholder="Enter VoX order GUID"
                    style={{ backgroundColor: '#f9fafb' }}
                    onKeyPress={(e) => { if (e.key === 'Enter') handleSearch(); }}
                  />
                </div>
                <div className="flex flex-column gap-2">
                  <label htmlFor="externalId">External ID</label>
                  <InputText
                    id="externalId"
                    value={externalId}
                    onChange={(e) => setExternalId(e.target.value)}
                    placeholder="Enter external ID"
                    style={{ backgroundColor: '#f9fafb' }}
                    onKeyPress={(e) => { if (e.key === 'Enter') handleSearch(); }}
                  />
                </div>
              </div>
            )}

            <div className="mt-2">
              <Button label="Search" icon="pi pi-search" onClick={handleSearch} />
            </div>
          </div>
          </Panel>

          {isSearchActive && (
            <div className="flex flex-column gap-2">
              <div className="flex justify-content-between align-items-center">
                <h3 className="font-semibold text-lg">Search Results</h3>
                <Button
                  icon="pi pi-refresh"
                  label="Refresh"
                  className="p-button-outlined p-button-sm"
                  onClick={() => activeQuery.refetch()}
                  loading={activeQuery.isRefetching}
                />
              </div>
              {activeQuery.isLoading && <div>Loading...</div>}
              {activeQuery.isError && (
                <div className="text-red-500">
                  Error loading search results: {activeQuery.error instanceof Error ? activeQuery.error.message : 'Unknown error'}
                </div>
              )}

              {searchMode === 'waterfall' && waterfallQuery.data && (
                <PrimeDataTable
                  value={waterfallQuery.data}
                  selectionMode="single"
                  onSelectionChange={handleWaterfallSelect}
                  paginator
                  rows={20}
                  emptyMessage="No waterfall executions found"
                  className="cursor-pointer"
                >
                  <Column field="dateStarted" header="DATE STARTED" body={formatWaterfallDateTime} sortable />
                  <Column field="waterfallName" header="WATERFALL" sortable />
                  <Column field="status" header="STATUS" body={waterfallStatusTemplate} sortable />
                  <Column field="loanNumber" header="LOAN NUMBER" sortable />
                  <Column field="loanIdentifier" header="LOAN IDENTIFIER" sortable />
                </PrimeDataTable>
              )}

              {searchMode === 'voxOrder' && voxQuery.data && (
                <PrimeDataTable
                  value={voxQuery.data}
                  selectionMode="single"
                  onSelectionChange={handleVoxOrderSelect}
                  paginator
                  rows={20}
                  emptyMessage="No VoX orders found"
                  className="cursor-pointer"
                >
                  <Column field="createdDate" header="DATE" body={formatVoxDateTime} sortable />
                  <Column field="integrationVendor" header="VENDOR" sortable />
                  <Column field="transactionName" header="TRANSACTION" sortable />
                  <Column field="status" header="STATUS" body={voxStatusTemplate} sortable />
                  <Column field="loanNumber" header="LOAN NUMBER" sortable />
                  <Column field="loanIdentifier" header="LOAN IDENTIFIER" sortable />
                </PrimeDataTable>
              )}
            </div>
          )}
        </div>
      </AppCard>
    </AppPage>
  );
};

export default VoxTracingSearch;
